import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { X, Check, AlertTriangle, ScanLine } from "lucide-react";

const CONTATTI = [
  ["nome", "Nome"], ["cognome", "Cognome"], ["email", "Email"], ["telefono", "Telefono"],
];

// Revisione dei dati estratti da uno o più documenti prima di applicarli all'anagrafica.
export default function ImportReviewDialog({ estratti, files, currentValues = {}, onApply, onClose }) {
  const { data } = useQuery({ queryKey: ["anagrafica-fields"], queryFn: async () => (await api.get("/anagrafica-fields")).data });
  const labels = Object.fromEntries([...CONTATTI, ...((data?.fields || []).map((f) => [f.key, f.label]))]);
  const keys = Object.keys(estratti || {});
  const conflitto = (k) => {
    const cur = String(currentValues[k] ?? "").trim();
    return cur && cur.toUpperCase() !== String(estratti[k] ?? "").trim().toUpperCase();
  };
  const [sel, setSel] = useState(() => Object.fromEntries(keys.map((k) => [k, !conflitto(k)])));
  const nConflitti = keys.filter(conflitto).length;
  const nSel = keys.filter((k) => sel[k]).length;

  const applica = () => {
    onApply(Object.fromEntries(keys.filter((k) => sel[k]).map((k) => [k, estratti[k]])));
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[80] bg-[#1a1a1a]/50 flex items-center justify-center p-4" data-testid="import-review-dialog">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl border-t-2 border-[#C9A84C] shadow-lg max-h-[90vh] flex flex-col">
        <div className="flex items-start justify-between px-6 py-3 border-b border-[#1A2B4A]/10">
          <div>
            <p className="text-xs uppercase tracking-[0.15em] text-[#4a5568]">Dati letti dai documenti</p>
            <h3 className="font-serif text-xl text-[#1A2B4A]">
              {keys.length} campi da {(files || []).length} documento{(files || []).length === 1 ? "" : "i"}
            </h3>
          </div>
          <button data-testid="import-review-close" onClick={onClose} className="text-[#1A2B4A] hover:text-[#9b2c2c]"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-3">
          {(files || []).length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-4" data-testid="import-review-files">
              {files.map((f, i) => (
                <span key={`${f.filename}-${i}`} className="inline-flex items-center gap-1.5 text-[11px] bg-[#fbf7ec] border border-[#C9A84C]/40 text-[#8a6d1f] rounded-md px-2.5 py-1">
                  <ScanLine className="w-3 h-3" /> {f.tipo || f.filename || "Documento"}
                  {typeof f.n === "number" && <b>· {f.n} campi</b>}
                </span>
              ))}
            </div>
          )}

          {nConflitti > 0 && (
            <div className="mb-3 flex items-start gap-2 rounded-md border border-[#C9A84C]/50 bg-[#fbf7ec] px-3 py-2" data-testid="import-review-warning">
              <AlertTriangle className="w-4 h-4 text-[#8a6d1f] shrink-0 mt-0.5" strokeWidth={1.8} />
              <p className="text-xs text-[#8a6d1f]">
                {nConflitti} campo{nConflitti === 1 ? "" : "i"} già compilato{nConflitti === 1 ? "" : "i"} con un valore diverso:
                lascio il valore attuale, spunta la riga se vuoi sostituirlo.
              </p>
            </div>
          )}

          {keys.length === 0 ? (
            <p className="text-sm text-[#4a5568]">Nessun dato riconosciuto nei documenti: compila i campi a mano.</p>
          ) : (
            <ul className="divide-y divide-[#1A2B4A]/5" data-testid="import-review-list">
              {keys.map((k) => {
                const cur = String(currentValues[k] ?? "").trim();
                const isConf = conflitto(k);
                return (
                  <li key={k} className="flex items-start gap-3 py-2.5">
                    <input data-testid={`import-review-check-${k}`} type="checkbox" checked={!!sel[k]}
                      onChange={(e) => setSel({ ...sel, [k]: e.target.checked })}
                      className="mt-1 w-4 h-4 accent-[#C9A84C] shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-[10px] font-medium uppercase tracking-[0.08em] text-[#8a93a6]">{labels[k] || k}</div>
                      <div className="text-sm text-[#1A2B4A] break-words">{String(estratti[k])}</div>
                      {cur && (
                        <div className={`text-[11px] mt-0.5 ${isConf ? "text-[#9b2c2c]" : "text-[#4a5568]"}`}>
                          valore attuale: {cur}
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-3 border-t border-[#1A2B4A]/10">
          <div className="flex gap-3 text-xs">
            <button data-testid="import-review-all" onClick={() => setSel(Object.fromEntries(keys.map((k) => [k, true])))}
              className="underline text-[#4a5568] hover:text-[#1A2B4A]">Seleziona tutti</button>
            <button data-testid="import-review-none" onClick={() => setSel(Object.fromEntries(keys.map((k) => [k, false])))}
              className="underline text-[#4a5568] hover:text-[#1A2B4A]">Nessuno</button>
          </div>
          <div className="flex gap-3">
            <button data-testid="import-review-cancel" onClick={onClose}
              className="px-4 py-2 text-sm border border-[#1A2B4A] rounded-sm hover:bg-[#f9f8f5]">Annulla</button>
            <button data-testid="import-review-apply" onClick={applica} disabled={nSel === 0}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-[#1A2B4A] text-white rounded-sm hover:bg-[#22345c] disabled:opacity-40">
              <Check className="w-4 h-4" /> Applica {nSel} campi
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
